import { Spinner } from "@components/Loading/Spinner";
import { useTranslation } from "@helpers/useTranslation";
import { FC, useContext, useEffect } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { useNavigate, useParams } from "react-router-dom";
import { FirebaseContext } from "src/helpers/firebase";
import { usePersistedState } from "src/helpers/usePersistedState";
import { useToast } from "src/helpers/useToast";

type Props = {
  pin: string;
  teamId: string | null;
};

/**
 * Handles a challenge QR deeplink (`?p=<encoded pin>` + optional `&teamId=`) for a visitor without
 * a session. With a teamId we authenticate straight into that (passwordless) team and land on the
 * challenge. Without one we persist the pin and the intended destination, and
 * send the visitor through the normal landing funnel — the team pages bring them back afterwards.
 */
export const DeeplinkJoin: FC<Props> = ({ pin, teamId }) => {
  const { t } = useTranslation("selectteam");
  const { eventId, challengeId } = useParams();
  const { pinAuth, functions } = useContext(FirebaseContext);
  const [appData, setAppData] = usePersistedState();
  const [exchangePinForToken] = useHttpsCallable(functions, "exchangePinForTokenV2");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const join = async () => {
      if (!teamId) {
        // No team in the link: save the pin + destination and go through the normal funnel
        setAppData({ ...appData, pin, deeplink: `/challenges/${challengeId}` });
        navigate(`/${eventId}`, { replace: true });
        return;
      }

      try {
        const result = await exchangePinForToken({ pin, eventId, teamId, isNew: false, isUploader: false });
        const token = result?.data as string;
        if (!token) throw new Error("No token returned");
        const res = await pinAuth(token);
        const uid = res.user?.uid;
        setAppData({ ...appData, pin, uid, isUploader: false });
        // No reload needed: signing in flips useClaims to loading, which unmounts the route tree
        // and remounts it with the freshly persisted uid once claims resolve — rendering the
        // challenge. This navigate only strips the ?p/&teamId params from the URL.
        navigate(`/${eventId}/challenges/${challengeId}`, { replace: true });
      } catch (error) {
        console.error("Deeplink join failed:", error);
        toast(t("somethingWentWrong"), "🤷‍♂️", "error");
        setAppData({ ...appData, pin });
        navigate(`/${eventId}`, { replace: true });
      }
    };
    join();
  }, []);

  return (
    <main className="flex-1 flex-col">
      <Spinner />
    </main>
  );
};
